/**
 * 앱 레이아웃 (Stripe Dashboard형 — TopBar + Sidebar + Content)
 * UI_STANDARD_APPLICATION_PLAN.md §5 Phase 1.3
 * H4: 모바일 Drawer·카드 레이아웃 · H3: 전역 검색 Provider·단축키
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Layout, Drawer } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import { BUSINESS_GENESIS_TOUR_PATH, hasGenesisWelcomePending } from '@shared/tenant-host';
import { AppTopBar } from './AppTopBar';
import { AppSidebar } from './AppSidebar';
import { BusinessMessageBridge } from './BusinessMessageBridge';
import { GlobalSearchModal } from './GlobalSearchModal';
import { PaletteExplainDrawer } from './PaletteExplainDrawer';
import { GlobalSearchProvider, useGlobalSearch } from '../../contexts/GlobalSearchContext';
import { useTenant } from '../../contexts/TenantContext';
import { useRealtimeNotifications } from '@frontend/domain-hooks/conscious/useRealtimeNotifications';
import { MOBILE_BREAKPOINT_PX } from '../../constants/layoutBreakpoints';
import './AppLayout.css';

const { Content } = Layout;

/** P7.5-2 — unified workbench reference slice (PTG reference + order pilot + raw-material/appeal GA). */
const UNIFIED_WORKBENCH_ROUTE =
  /^\/(?:item\/(?:sellable|service|asset|consumable)(?:\/[^/]+)?|item\/raw-material(?:\/[^/]+)?|entity\/internal(?:\/[^/]+)?|entity\/external(?:\/(?!create(?:\/|$))[^/]+)?|entity\/supplier(?:\/[^/]+)?|manufacturing\/(?:bom|production-order|quality-inspection)(?:\/[^/]+)?|space\/(?:physical|virtual)(?:\/[^/]+)?|knowledge\/(?:report|policy|plan)(?:\/[^/]+)?|finance\/(?:state|flow|budget|vouchers|receivables|overdue)(?:\/[^/]+)?|finance\/voucher\/[^/]+\/new|finance-advanced\/(?:legal-entity|currency-rate|intercompany)(?:\/[^/]+)?|appeal\/(?:semantic-formation|ephemeral-formation|ephemeral-performance|recurrent-performance)(?:\/[^/]+)?|calendar\/(?:event|trigger|cycle)(?:\/[^/]+)?|event\/(?:flow-definition|stream-definition|action-binding|flow-execution|stream-execution|flow-event|stream-event)(?:\/[^/]+)?|log\/(?:flow|stream)(?:\/[^/]+)?|warehouse\/(?:picking-list|receiving|putaway)(?:\/[^/]+)?|inventory\/(?:shortage|stock|movements|adjustments)(?:\/[^/]+)?)$/;

export function isSellableWorkbenchRoute(pathname: string): boolean {
  return UNIFIED_WORKBENCH_ROUTE.test(pathname);
}

export interface AppLayoutProps {
  children: React.ReactNode;
}

function isMobileViewport(): boolean {
  return typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT_PX;
}

const AppLayoutInner: React.FC<AppLayoutProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentTenant } = useTenant();
  const { openSearch } = useGlobalSearch();

  useRealtimeNotifications(currentTenant?.id ?? '');
  /** Desktop: expanded primary area nav (founder staging discoverability). */
  const effectiveSidebarCollapsed = false;

  const isWorkbenchFullBleed = isSellableWorkbenchRoute(location.pathname);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(isMobileViewport);
  /** Desktop always shows primary navigation; mobile uses Drawer + hamburger. */
  const hideDesktopSidebar = false;

  useEffect(() => {
    const onResize = () => {
      const mobile = window.innerWidth < MOBILE_BREAKPOINT_PX;
      setIsMobile(mobile);
      if (!mobile) setMobileDrawerOpen(false);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    setMobileDrawerOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isWorkbenchFullBleed) {
      return;
    }
    document.documentElement.classList.add('wb-body-scroll-lock');
    const previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.documentElement.classList.remove('wb-body-scroll-lock');
      document.body.style.overflow = previousBodyOverflow;
    };
  }, [isWorkbenchFullBleed]);

  useEffect(() => {
    if (hasGenesisWelcomePending() && location.pathname !== BUSINESS_GENESIS_TOUR_PATH) {
      navigate(BUSINESS_GENESIS_TOUR_PATH, { replace: true });
    }
  }, [location.pathname, navigate]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const isShortcut = (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k';
      if (!isShortcut) return;
      const target = event.target;
      if (
        target instanceof HTMLElement &&
        (target.isContentEditable ||
          target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT')
      ) {
        return;
      }
      event.preventDefault();
      openSearch();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [openSearch]);

  const handleSidebarNavigate = useCallback(() => {
    if (isMobile) {
      setMobileDrawerOpen(false);
    }
  }, [isMobile]);

  const contentClassName = [
    'app-layout-content',
    isMobile ? 'app-layout-content--mobile' : 'app-layout-content--desktop',
    isWorkbenchFullBleed ? 'app-layout-content--workbench-full-bleed' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Layout
      className="business-shell"
      data-testid="business-app-layout-auth-depth"
      data-plane="business"
      data-auth-depth-plane="business"
    >
      <a href="#business-main-content" className="business-skip-link">
        본문으로 건너뛰기
      </a>
      <BusinessMessageBridge />
      <GlobalSearchModal />
      <PaletteExplainDrawer />
      <Layout.Header aria-label="앱 헤더" className="business-shell-header">
        <AppTopBar
          {...(isMobile ? { onMenuClick: () => setMobileDrawerOpen(true) } : {})}
          showMenuButton={isMobile}
        />
      </Layout.Header>

      {isMobile ? (
        <>
          <Drawer
            title="메뉴"
            placement="left"
            onClose={() => setMobileDrawerOpen(false)}
            open={mobileDrawerOpen}
            className="business-shell-drawer"
            width={280}
          >
            <AppSidebar collapsed={false} onNavigate={handleSidebarNavigate} inDrawer />
          </Drawer>
          <Content
            id="business-main-content"
            className={`${contentClassName} business-focus-ring`}
            role="main"
            aria-label="메인 콘텐츠"
            tabIndex={-1}
          >
            {children}
          </Content>
        </>
      ) : (
        <Layout
          {...(!hideDesktopSidebar ? { hasSider: true } : {})}
          className="business-shell-body"
        >
          {!hideDesktopSidebar ? (
            <AppSidebar collapsed={effectiveSidebarCollapsed} onNavigate={handleSidebarNavigate} />
          ) : null}
          <Layout>
            <Content
              id="business-main-content"
              className={`${contentClassName} business-focus-ring`}
              role="main"
              aria-label="메인 콘텐츠"
              tabIndex={-1}
            >
              {children}
            </Content>
          </Layout>
        </Layout>
      )}
    </Layout>
  );
};

export const AppLayout: React.FC<AppLayoutProps> = ({ children }) => (
  <GlobalSearchProvider>
    <AppLayoutInner>{children}</AppLayoutInner>
  </GlobalSearchProvider>
);

export default AppLayout;
